/** @jsxImportSource @emotion/react */
import type { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { Global } from "@emotion/react";
import faqSVG from "assets/images/faq.svg";
import {
  Callout,
  FlexRow,
  H2,
  H3,
  Link,
  ScreenReaderText,
  SiteContainer,
  Text,
} from "atoms";
import { constants } from "config";
import { Head } from "features/misc";
import { Accordion } from "./Accordion";
import { BackToTop, JumpNav } from "./JumpNavigation";

const globalCSS = (theme: Theme) => ({
  "html,body": {
    background: theme.colors.pink,
  },
});

const navItems = [
  {
    id: "general",
    label: "General",
  },
  {
    id: "thehug",
    label: "The HUG",
  },
  {
    id: "hugpass",
    label: "Hug Pass",
  },
  {
    id: "programs",
    label: "Programs",
  },
  {
    id: "grouphug",
    label: "groupHUG",
  },
  {
    id: "hedgehug",
    label: "hedgeHUG",
  },
  {
    id: "airhug",
    label: "airHUG",
  },
];

function FAQ() {
  return (
    <Main>
      <Head title="FAQ" />
      <Global styles={globalCSS} />

      <SiteContainer>
        <Header>
          <ScreenReaderText>
            <h1>
              Frequently Asked Questions
              <img src={faqSVG} alt="" aria-hidden="true" />
            </h1>
          </ScreenReaderText>
          <H2 size="h1">
            Are things still feeling
            <span>fuzzy?</span>
          </H2>

          <Text>Read our FAQ to get more details</Text>
        </Header>

        <JumpNav items={navItems} />

        <SCallout>
          <SectionHeader>
            <H2 id={navItems[0].id}>General Questions</H2>
            <BackToTop />
          </SectionHeader>

          <Accordion heading={<H3 size="sm">Who is the HUG team?</H3>}>
            <Text>
              HUG was founded in February 2022 by{" "}
              <Link href="https://twitter.com/randizuckerberg">
                Randi Zuckerberg
              </Link>{" "}
              and co-founded by{" "}
              <Link href="https://twitter.com/debsoon">Debbie Soon</Link>,
              alongside a team of passionate professionals with diverse
              backgrounds in technology, arts, entrepreneurship, and education.
              The entire team is motivated by a shared mission to uplift
              creators in Web3 and to help diverse entrepreneurs build the next
              generation of successful crypto-enabled businesses.{" "}
              <Link to="/about">Check out the rest of the team here.</Link>
            </Text>
          </Accordion>

          <Accordion heading={<H3 size="sm">Who is HUG for?</H3>}>
            <Text>
              HUG is for creators, curators, and collectors. We welcome
              everyone, but we especially want to help those who we believe have
              traditionally felt invisible in major waves of tech innovation
              (women, parents, non-native English speakers, BIPOC, people with
              disabilities and/or other chronic conditions, neurodivergent
              people, or LGBTQIA+ folks) to also have room on the front lines of
              this new digital revolution. We know how exciting, but also
              isolating and overwhelming Web3 can be, so we want to embrace as
              many creators, curators, or collectors as possible!
            </Text>
          </Accordion>

          <Accordion heading={<H3 size="sm">How did HUG start?</H3>}>
            <Text>
              Randi&apos;s unique career at the intersection of technology and
              the arts found her advising numerous Web3 entrepreneurs and, after
              noticing that many creators were facing the same issues, she saw
              the need to scale her impact. Randi then met Debbie on Discord
              (How Web3 is that?!) and they worked together, alongside the team
              at Assemble Stream, to dream up HUG. Our first project was
              groupHUG, a creator accelerator program aimed at helping to grow
              the next generation of successful crypto businesses led by diverse
              founders. But after receiving more than 100 applications within
              the first 24 hours of launching, we quickly realized that we
              needed to do even more to support our Web3 creators, hence the
              launch of The HUG––the world&apos;s friendliest community for
              discovering and reviewing Web3 projects and artists.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                What&apos;s the best way to not miss updates from HUG?
              </H3>
            }
          >
            <Text>
              Follow us on Twitter (
              <Link href="https://twitter.com/thehugxyz">@thehugxyz</Link>) and
              sign up for{" "}
              <Link to={{ hash: "newsletter" }}>email updates here</Link>. You
              can also join our <Link href={constants.discordURL}>Discord</Link>
              , which houses a number of holder-related announcements.
            </Text>
          </Accordion>

          <Accordion heading={<H3 size="sm">What is the inclusiverse?</H3>}>
            <Text>
              The &quot;inclusiverse&quot; is how we describe the world of
              HUG&#8212;an uplifting, inclusive universe where everyone is
              welcomed with open arms. In the inclusiverse, you&apos;ll come
              across creators, collectors, resources, tools, and an abundance of
              hugs. And you&apos;ll probably never want to go back to a regular
              universe ever again!
            </Text>
          </Accordion>

          <Accordion heading={<H3 size="sm">Can I get a hug?</H3>}>
            <Text>Of course. 🤗</Text>
          </Accordion>
        </SCallout>

        <SCallout>
          <SectionHeader>
            <H2 id={navItems[1].id}>The HUG</H2>
            <BackToTop />
          </SectionHeader>

          <Accordion heading={<H3 size="sm">What is The HUG?</H3>}>
            <Text>
              The HUG is a community review platform where we all come together
              to discover NFT projects and creators in a
              thoughtfully&#8212;curated way! Originally born out of the HUG
              Discord, the HUG now lives on the World Wide Web for everyone to
              see, although only HUG Pass holders will have full access.
            </Text>

            <Text>
              This means from now on, instead of DYOR&#8212;ing alone at your
              computer, you can curate, research, review, and share information
              on artists and projects, together with your HUGfam! The HUG has
              more than a dozen specialized categories of projects, and we are
              actively adding more of them, while working with our community to
              build out exciting new ways to curate and interact with the
              platform.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                What kind of creators and projects are included in the HUG?
              </H3>
            }
          >
            <Text>
              We want HUG to be the go-to source for community research and
              reviews for all NFT projects and creators in the future. But for
              now, we are focused on amplifying diverse creators and projects
              with a social mission, alongside more well&#8212;known projects
              that are more widely considered to be &quot;blue chip&quot;.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                How can I submit new creators and projects to the HUG?
              </H3>
            }
          >
            <Text>
              While the HUG is still in beta, you can nominate projects as well
              as artists via the following forms:
            </Text>
            <Text as="ul">
              <li>
                <Link href="https://forms.gle/mPHT7e5Esgz2aq6z8">
                  Project nomination form
                </Link>
              </li>
              <li>
                <Link href="https://forms.gle/saUZrJsDoA9JfvGx5">
                  Artist nomination form
                </Link>
              </li>
            </Text>
            <Text>
              All project and artist nominations are then voted in by our
              community prior to being published onto the platform.
            </Text>
            <Text>
              Within a couple of months, we will be moving off Google Forms and
              you will then be able to submit this information directly through
              the HUG.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                Who decides which projects and artists make it to the HUG and
                how?
              </H3>
            }
          >
            <Text>
              All projects and artists currently featured on the HUG are voted
              in by our community. At the moment, we put nominated projects up
              for voting in our Discord every couple of weeks, but in the future
              all of this will be done natively on the HUG platform.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">What is the benefit of being on the HUG?</H3>
            }
          >
            <Text>
              We believe that the NFT space is in need of more trust and
              curation. At the same time, trying to beat the algorithm on
              Twitter to get noticed and discovered is also an endlessly tiring
              process. Being listed on the HUG means that the ins and outs of
              your project will be researched and shared in an
              easy-to-understand one pager, while also being neatly categorized
              according to our specially curated tags. Likewise, if you are an
              artist, the HUG is another great platform to help you get
              discovered and to share your story as well as collections in a
              single place.
            </Text>
          </Accordion>
        </SCallout>

        <SCallout>
          <SectionHeader>
            <H2 id={navItems[2].id}>The HUG Pass</H2>
            <BackToTop />
          </SectionHeader>

          <Accordion heading={<H3 size="sm">What is the HUG Pass?</H3>}>
            <Text>
              The HUG Pass is a NFT that features huggable, collectible artwork
              and grants you full access to the HUG. It also crowns you as a
              Hugger, i.e. an official member of our HUG community, where you
              get to reap other benefits such as prioritization in the selection
              process for our creator accelerator programs, perks from current
              and future partnership opportunities, IRL events, dedicated time
              with Randi and Debbie, and more.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                What are the benefits come from owning a HUG Pass?
              </H3>
            }
          >
            <Text>
              Aside from giving you access to the HUG community review platform,
              we are constantly looking to deliver additional value to our
              holders. In particular, we want to do everything we can to support
              creators, whether that be telling your story and spotlighting your
              creations, helping you find your next collector, sharing
              educational resources and tools, mentorship and advice from Randi
              and Debbie, and more. Of course, we also like to have fun, so you
              can be sure that there will be plenty more huggable experiences in
              the future. Last but not least, we are constantly in talks with
              other projects and businesses to continue bringing exciting
              partnership opportunities your way.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                Is there any benefit to owning more than 1 HUG Pass?
              </H3>
            }
          >
            <Text>
              Yes! Owning a matching pair of Hugs (i.e. left and right of the
              same kind of hug) unlocks access to various microcommunities, each
              organized along their own interests and perks. For more details on
              what each type of Hug unlocks, refer to our{" "}
              <Link href={constants.mediumArticle}>Medium article</Link>.
            </Text>
          </Accordion>

          <Accordion
            heading={<H3 size="sm">How many HUG Passes are there?</H3>}
          >
            <Text>
              There are presently around 6,000 HUG Passes that have been minted.
              Our current contract has a maximum supply of 15,000 HUG Passes,
              which we from day 1 had intended to mint in multiple waves. This
              was done deliberately in order to: (i) onboard new people into the
              HUG community in a manageable way, (ii) better facilitate early
              Huggers getting to know each other as well as contribute to our
              HUG build, while also (iii) not overly disadvantaging those who
              may not have known about us from inception with the aim of
              maintaining accessibility to our community.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                When do you intend to mint the remaining HUG Passes?
              </H3>
            }
          >
            <Text>
              We currently have not finalized any plans to mint additional HUG
              Passes. We promise to do so in communication and consultation with
              existing holders, but will always prioritize opening up access to
              and usage of the HUG to as many people as possible, especially
              creators and collectors from marginalized communities. Also, for
              security reasons, please note that we will never ever do a stealth
              mint, and that any mint-related announcements will be made across
              multiple official channels (Twitter, Discord, and email) with
              plenty of notice. There are three ways to get on our initial mint
              list:
            </Text>
          </Accordion>
        </SCallout>

        <SCallout>
          <SectionHeader>
            <H2 id={navItems[3].id}>HUG&apos;s Creator Accelerator Programs</H2>
            <BackToTop />
          </SectionHeader>

          <Accordion
            heading={
              <H3 size="sm">
                What creator accelerator programs does HUG have?
              </H3>
            }
          >
            <Text>
              We currently have three creator accelerator programs &#8212;
              groupHUG (for projects that have either started or already
              finished minting), hedgeHUG (for projects that have not yet
              minted), and airHUG (our artist-in-residence program for
              individual artists).
            </Text>
            <Text>
              For more details on the individual programs, please refer to the
              sections below.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                What makes HUG&apos;s creator accelerator programs different
                from others?
              </H3>
            }
          >
            <Text>
              We believe that in order to truly advise creators in this space,
              you have to understand what it means to be a creator yourself and
              that NFT success comes from having an entrepreneurial brain and an
              artist&apos;s heart. HUG&apos;s founding team is made up of not
              only leaders in business and entrepreneurship but also acclaimed
              artists themselves, having won multiple artistic awards, performed
              on Broadway, started artistic programs in refugee camps, and
              advised some of the biggest names in both the arts and technology.
              We believe you will not meet another team in Web3 that cares so
              passionately about the intersection of the arts and tech - and how
              those two can be successfully combined to be at the forefront of
              this new exciting world. Lastly, our combined social media reach
              of 650K (and growing!) doesn&apos;t hurt, and we hope to include
              that in our work with you to grow your own community in an organic
              and authentic manner.
            </Text>
          </Accordion>

          <Accordion heading={<H3 size="sm">Do I get to work with Randi?</H3>}>
            <Text>
              Yes, every project and artist that goes through our creator
              accelerator programs will get a certain number of 1:1 office hours
              with Randi and Debbie, while also getting dedicated attention from
              our Creator Success team.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                Is there a fee for being a part of your creator accelerator
                programs?
              </H3>
            }
          >
            <Text>
              Yes, there is. Fees vary by program, and more detail is offered in
              the sections below.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                How can I apply for your creator accelerator programs?
              </H3>
            }
          >
            {constants.applicationsOpen ? (
              <>
                <Text>
                  Applications will open on {constants.applyStartDate} and close
                  on {constants.applyEndDate}. The interview process will begin
                  in September, with all of our next programs currently planned
                  to start in October. If you have an upcoming mint, applicants
                  should be targeting a mint date no earlier than the end of
                  October.
                </Text>
                <Link href={constants.applyUrl} arrow="dr" variant="gradient">
                  Apply Now
                </Link>
              </>
            ) : (
              <Text>
                Applications are currently closed. Stay tuned to find our about
                your next opportunity to apply.
              </Text>
            )}
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">
                What if I don&apos;t get selected this time round?
              </H3>
            }
          >
            <Text>
              Don&apos;t give up! Our goal is to meet creators where they are at
              in Web3, so come join our community where we provide multiple
              access points and tools to support creators in the space.
            </Text>
            <Text>
              Also, if it wasn&apos;t a perfect fit this time, keep building. We
              love to see progress on your art work or project and to learn
              about how you are evolving along the way. And keep an eye out for
              our next round of applications.
            </Text>
          </Accordion>
        </SCallout>

        <SCallout>
          <SectionHeader>
            <H2 id={navItems[4].id}>groupHUG</H2>
            <BackToTop />
          </SectionHeader>

          <Accordion heading={<H3 size="sm">What is groupHUG?</H3>}>
            <Text>
              groupHUG is a growth accelerator for projects that have already
              commenced or gone through a minting process. Through direct
              mentorship from HUG&apos;s senior advisors and founding team,
              which includes Randi Zuckerberg and Debbie Soon, and a four week
              curriculum, we work together with creators to strive for long-term
              growth and financial success.
            </Text>
          </Accordion>
          <Accordion heading={<H3 size="sm">Who is groupHUG for?</H3>}>
            <Text>
              groupHUG is for NFT projects that have already built a substantial
              community and gone through a mint process (either in the process
              of minting or have already sold out), but are now in search of
              additional guidance on how to create long-term success in the Web3
              space. If you are pre-mint, please check out our offering for
              hedgeHUG.
            </Text>
          </Accordion>
          <Accordion heading={<H3 size="sm">How does groupHUG work?</H3>}>
            <Text>
              Each groupHUG cohort begins with a four-week intensive curriculum
              covering topics essential for both strengthening the fundamentals
              of post-mint projects as well as taking them to the next level. At
              the end of the four weeks, groupHUG projects will have had the
              opportunity to create key deliverables designed to lay the
              groundwork for executing a focused plan, with ongoing support
              through 1:1 advising, networking opportunities, and group
              sessions.
            </Text>
            <Text>In summary, groupHUG projects have access to:</Text>
            <Text as="ul">
              <li>
                Six months of mentorship and advice from HUG&apos;s expert team
              </li>
              <li>
                Four weeks of curriculum about key Web3 topics such as
                operations and strategy, risk management, community building,
                and more
              </li>
              <li>
                1:1 office hours with HUG&apos;s senior advisors and founding
                team
              </li>
              <li>
                Networking opportunities within the HUGfam and partner
                communities
              </li>
              <li>
                AMA sessions and workshops with industry experts on a variety of
                topics including communication, conflict resolution, public
                speaking, smart contracts, security, and more
              </li>
            </Text>
          </Accordion>
          <Accordion
            heading={
              <H3 size="sm">
                How do you pick which projects get to be a part of groupHUG?
              </H3>
            }
          >
            <Text>
              Even though we want to hug everyone, we get many more applications
              than we can accommodate for our cohorts. That being said, we go
              through each and every one of them. When it comes to selecting
              participants, here is the framework we follow:
            </Text>
            <Text as="ul">
              <li>
                Team: How resilient is the founding team and how committed are
                they to their mission? Founder-mission fit is very important to
                us and in past cohorts, we&apos;ve worked with creators who have
                been advocating for specific causes for years.
              </li>
              <li>
                Art: Is it original? Does it strike an emotional chord that is
                different from anything else out there?
              </li>
              <li>
                Community: Is there a community that&apos;s tied to the
                project&apos;s mission and values? Have the projects clearly
                defined and lived by their values since Day 1?
              </li>
              <li>
                Plan: With numerous NFT projects being launched every day, what
                sets yours apart? What is the ultimate goal?
              </li>
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">What are the terms of being in groupHUG?</H3>
            }
          >
            <Text>
              groupHUG participation is for 6 months during which we charge a
              $5,000 participation fee and a percentage of mint proceeds for any
              collections still minting and/or for any collection launched
              within the year.
            </Text>
            <Text>
              Exact percentages are discussed individually with each creator to
              determine an arrangement that makes sense for their project and
              their required support from us.
            </Text>
          </Accordion>

          <Accordion
            heading={
              <H3 size="sm">What kind of projects work with groupHUG?</H3>
            }
          >
            <Text>
              Our list keeps growing, but we&apos;ve worked with incredible
              projects such as Boss Beauties (we even helped them raise their
              $4M+ venture capital round!), Curious Addys, Sad Girls Bar, Sacred
              Skulls, Eyes of Fashion, WOW Pixies, Solana project Infinity Labs,
              and more.
            </Text>
          </Accordion>
        </SCallout>

        <SCallout>
          <SectionHeader>
            <H2 id={navItems[5].id}>hedgeHUG</H2>
            <BackToTop />
          </SectionHeader>

          <Accordion heading={<H3 size="sm">What is hedgeHUG?</H3>}>
            <Text>
              hedgeHUG is a hands-on advisory program for pre-mint projects. We
              typically encourage projects to mint 2-3 months into the program
              so we can provide support before and after the mint.{" "}
            </Text>
            <Text>
              On top of 1:1 mentorship from HUG&apos;s senior advisors and
              founding team, which includes Randi and Debbie, we also leverage
              our creativity and the love across our social media channels of
              650K+ to lay the groundwork for long-term success, from mint to
              beyond.
            </Text>
          </Accordion>
          <Accordion heading={<H3 size="sm">Who is hedgeHUG for?</H3>}>
            <Text>
              hedgeHUG is typically for teams who have committed to launching a
              NFT project in the next 2-3 months, have started building a
              community, and have already thought through a v1 of their roadmap.{" "}
            </Text>
            <Text>
              These projects would be looking to connect with like-minded
              creators, want mentorship and advice to further refine their
              business and minting strategy, as well as for additional exposure
              in the lead up to their mint.
            </Text>
            <Text>
              hedgeHUG is also for projects who are in it for long-term success,
              not just a quick win.
            </Text>
          </Accordion>
          <Accordion heading={<H3 size="sm">How does hedgeHUG work?</H3>}>
            <Text>
              hedgeHUG is a personalized six-month collaboration that begins
              with a four-week intensive curriculum, including training in
              customer/community discovery, brand development, media planning,
              go-to-market strategy, and more.{" "}
            </Text>
            <Text>
              After the four week curriculum, there are advanced modules for
              accountability, weekly strategy check-ins, and the ability to
              leverage our social media reach of over 650K to lend additional
              support leading up to the mint. hedgeHUG projects also get access
              to 1:1 advising through office hours with HUG&apos;s senior
              advisors and founding team, which includes Randi and Debbie.
            </Text>
          </Accordion>
          <Accordion
            heading={
              <H3 size="sm">
                How do you pick which projects get to be a hedgeHUG?
              </H3>
            }
          >
            <Text>
              Even though we want to hug everyone, we get many more applications
              than we can accommodate for our cohorts. That being said, we go
              through each and every one of them. When it comes to selecting
              participants, here is the framework we follow:
            </Text>
            <Text as="ul">
              <li>
                Team: How resilient is the founding team and how committed are
                they to their mission? Founder-mission fit is very important to
                us and in past cohorts, we&apos;ve worked with creators who have
                been advocating for specific causes for years.
              </li>
              <li>
                Art: Is it original? Does it strike an emotional chord that is
                different from anything else out there?
              </li>
              <li>
                Community: Is there a community that&apos;s tied to the
                project&apos;s mission and values? Have the projects clearly
                defined and lived by their values since Day 1?
              </li>
              <li>
                Plan: With numerous NFT projects being launched every day, what
                sets yours apart? What is the ultimate goal?
              </li>
            </Text>
          </Accordion>
          <Accordion
            heading={
              <H3 size="sm">
                What happens after a hedgeHUG project has minted?
              </H3>
            }
          >
            <Text>
              We consider hedgeHUG projects to be a part of the HUGfam and
              prioritize participants when evaluating potential future
              collaborations. If you are interested in additional support
              post-mint, you will also have priority access to apply for a
              future groupHUG cohort!
            </Text>
          </Accordion>
          <Accordion
            heading={<H3 size="sm">What are the terms of being a hedgeHUG?</H3>}
          >
            <Text>
              hedgeHUG participation is for 6 months during which we charge a
              $3000 participation fee and a percentage of mint proceeds for any
              collection launched within the year.{" "}
            </Text>
            <Text>
              Exact percentages are discussed individually with each creator to
              determine an arrangement that makes sense for their project and
              their required support from us.
            </Text>
          </Accordion>
          <Accordion
            heading={<H3 size="sm">Why is the name hedgeHUG so good?</H3>}
            css={{ border: 0 }}
          >
            <Text>🤗🤗🤗</Text>
          </Accordion>
        </SCallout>

        <SCallout>
          <SectionHeader>
            <H2 id={navItems[6].id}>airHug (Artists-In-Residence)</H2>
            <BackToTop />
          </SectionHeader>

          <Accordion heading={<H3 size="sm">What is airHUG?</H3>}>
            <Text>
              airHUG is an artist-in-residence and Web3 accelerator program for
              up-and-coming 1/1 artists of any medium, from theater artists,
              poets, photographers, to musicians and more.
            </Text>
            <Text>
              This program is application only - sponsored entirely through
              partnerships, where artists receive a scholarship to amplify and
              grow their brand while they focus on their art. Based on our
              team&apos;s experience as investors and producers in both
              theatrical content and early/growth stage tech companies that
              support innovation in the arts, we leverage that expertise to help
              artists build sustainable and exciting brands on the forefront of
              Web3. Our team&apos;s work has been featured on Broadway,
              Off-Broadway, and television - including multiple Tony Awards and
              collaborations with companies such as Jim Henson Productions, NBC,
              and some of the leading artists of our time.{" "}
            </Text>
          </Accordion>
          <Accordion heading={<H3 size="sm">Who is airHUG for?</H3>}>
            <Text>
              airHUG is for up-and-coming artists of all kinds— 1/1 digital
              illustrators, poets, musicians, and theater artists, and more.{" "}
            </Text>
            <Text>airHUG is for you if…</Text>
            <Text as="ol">
              <li>
                You are in it for long-term success, not just a quick win.
              </li>
              <li>
                You want to build community, feel a bit lost, and want to
                connect with like-minded artists and creators.
              </li>
              <li>
                You want to be seen as a thought leader and be on the forefront
                of the new creator economy in Web3.
              </li>
            </Text>
          </Accordion>
          <Accordion heading={<H3 size="sm">How does airHUG work?</H3>}>
            <Text>
              Each airHUG cohort begins with a four-week intensive curriculum
              covering topics essential for both strengthening the fundamentals
              of post-mint projects as well as taking them to the next level. At
              the end of the four weeks, airHUG projects will have had the
              opportunity to create key deliverables designed to lay the
              groundwork for executing a focused plan, with ongoing support
              through 1:1 advising, networking opportunities, and group
              sessions.
            </Text>
            <Text>In summary, airHUG projects have access to:</Text>
            <Text as="ul">
              <li>
                Six months of mentorship and advice from HUG&apos;s expert team
              </li>
              <li>
                Four weeks of curriculum about key Web3 topics such as
                operations and strategy, risk management, community building,
                and more
              </li>
              <li>
                1:1 office hours with HUG&apos;s senior advisors and founding
                team
              </li>
              <li>
                Networking opportunities within the HUGfam and partner
                communities
              </li>
              <li>
                AMA sessions and workshops with industry experts on a variety of
                topics including communication, conflict resolution, public
                speaking, smart contracts, security, and more
              </li>
              <li>
                Being able to showcase their work more widely to the HUG
                community
              </li>
            </Text>
          </Accordion>
          <Accordion
            heading={
              <H3 size="sm">
                How do you pick which projects get to be part of the next
                cohort?
              </H3>
            }
          >
            <Text>
              Even though we want to hug everyone, we get many more applications
              than we can accommodate for our cohorts. That being said, we go
              through each and every one of them. When it comes to selecting
              participants, here is the framework we follow:
            </Text>
            <Text as="ul">
              <li>
                Art: Is it original? Does it strike an emotional chord that is
                different from anything else out there?
              </li>
              <li>
                Artist: How resilient is the artist and how committed are they
                to their mission? Through our program, we help strengthen an
                artist&apos;s mission to amplify their brand, work, and identity
                in web3
              </li>
              <li>
                Community: Is there a community tied to the artist&apos;s
                mission and values? Does the artist have a cohesive value system
                on the creation of their work?
              </li>
              <li>
                Plan: With numerous NFT projects being launched every day, what
                sets yours apart? What is the ultimate goal? Why do you want to
                join the incubator?
              </li>
            </Text>
          </Accordion>
          <Accordion
            heading={<H3 size="sm">What kind of artists work with airHUG?</H3>}
          >
            <Text>
              Our list keeps growing, but we&apos;ve worked with leading 1:1
              artists such as Leah Sams and Chipped Pixels in the Web3 space to
              established artists who are looking to expand their work from Web2
              to Web3.{" "}
            </Text>
          </Accordion>
          <Accordion
            heading={
              <H3 size="sm">What are the terms for being a part of airHUG?</H3>
            }
          >
            <Text>
              airHUG participation is for 6 months is free and given as a
              scholarship. In addition, we typically charge a percentage of mint
              proceeds for any new collection launched within the program term.{" "}
            </Text>
          </Accordion>
        </SCallout>
      </SiteContainer>
    </Main>
  );
}

const Main = styled.main(({ theme }) => ({
  background: theme.colors.pink,
}));

const Header = styled.header(({ theme }) => ({
  background: `radial-gradient(circle at center center, ${theme.colors.purple}, rgba(255, 255, 255, 0) 30%)`,
  padding: "0 0 2rem",
  position: "relative",
  textAlign: "center",
  span: {
    display: "block",
    filter: "blur(1px)",
    fontSize: "5rem",
    lineHeight: "1",
    margin: "0 1rem 1rem",
    textTransform: "uppercase",
    "@media (min-width: 800px)": {
      fontSize: "8rem",
    },
  },
  img: {
    position: "absolute",
    left: "1vw",
    top: "2vw",
    width: "min(16rem, max(16vw, 8rem))",
  },
  h2: {
    marginTop: 0,
  },
}));

const SCallout = styled(Callout)({
  padding: "2rem 1rem",
  "@media (min-width: 800px)": {
    padding: "3rem 2rem",
  },
  "& + &": {
    marginTop: "3rem",
  },
  "details:last-of-type": {
    borderBottom: 0,
  },
});

const SectionHeader = styled(FlexRow)({
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 10,
  marginBottom: "1rem",
  "&>h2": {
    display: "inline-block",
    margin: "0 0 .33rem 0",
  },
  "&>a": {
    flex: "0 0 100%",
    fontSize: "15px",
    "@media (min-width: 600px)": {
      flex: "0 0 auto",
    },
  },
}).withComponent("header");

export { FAQ };
